import { designVariables } from "@studio3t/design-variables";
import {
  GenericDropdown,
  GenericDropdownContext,
  Icon,
  IconSVG,
  type ListItemProps,
  SVGColorKey,
} from "@studio3t/generic-components";
import styled from "styled-components";

// The editors' status-bar pickers (Figma Bridge_PipelineCode StatusBar
// 16389-156752 / 16389-131398): a small subtle button reading the current
// value with a 10px chevron, opening a 100px menu (DropdownMenu
// 16389-156560) above it, right edges aligned. Shared by the source row's
// "Source delivers" control and the schema editor's encoding picker.

// Figma DropdownMenu (16389-156560): the options in a 100px menu.
export const FOOTER_MENU_WIDTH = 100;

export function FooterPicker({
  label,
  name,
  items,
  disabled = false,
  menuWidth = FOOTER_MENU_WIDTH,
  testId,
}: {
  /** The trigger's visible text: the current value, nothing more. */
  label: string;
  /** The trigger's accessible name, carrying the context the bare label
   *  drops ("Source delivers: Avro"). */
  name: string;
  items: ListItemProps[][];
  disabled?: boolean;
  /** The menu's width; the encoding menus' 100px unless the rows carry
   *  more (the variant picker's 260px, Figma 16421-180155). */
  menuWidth?: number;
  testId: string;
}) {
  return (
    <Fit>
      <GenericDropdown
        dropdownItems={items}
        width={{ strategy: "fixed", size: menuWidth }}
        // Right edges aligned: the menu opens above the trigger, flush with
        // its right side.
        placement="top-end"
        disabled={disabled}
      >
        <GenericDropdownContext.Consumer>
          {({ open }) => (
            <FooterTrigger
              type="button"
              $active={open}
              aria-label={name}
              aria-disabled={disabled || undefined}
              data-testid={testId}
            >
              {label}
              <IconSVG
                icon={Icon.BaseChevron}
                size={10}
                svgColorKey={SVGColorKey.semanticTextBody}
              />
            </FooterTrigger>
          )}
        </GenericDropdownContext.Consumer>
      </GenericDropdown>
    </Fit>
  );
}

// The lib's dropdown root otherwise stretches to the footer row, pushing
// the cursor readout off the trigger's own width.
const Fit = styled.span`
  display: inline-flex;

  > div {
    width: auto;
  }
`;

const BUTTON = designVariables.ui.button;
const SINGLE_LINE = designVariables.type.singleLine;

// Same subtle trigger as SchemaVariantDropdown's VariantTrigger (small
// button metrics, 10px chevron). aria-disabled = nothing to pick right now;
// the mock's disabled "BSON" samples as the semantic disabled text color.
// Disabled for assistive tech and styling, not natively: a disabled button
// cannot take focus and gets no pointer events in WebKit, so a tooltip on
// it would be unreachable by keyboard everywhere and by mouse in Safari.
export const FooterTrigger = styled.button<{ $active?: boolean }>`
  box-sizing: border-box;
  display: inline-flex;
  align-items: center;
  gap: ${BUTTON.gap}px;
  height: ${BUTTON.s.size}px;
  padding: 0 ${BUTTON.s.paddingw}px;
  border: none;
  border-radius: ${BUTTON.corner}px;
  font-size: ${SINGLE_LINE.small.medium.fontSize}px;
  font-weight: ${SINGLE_LINE.small.medium.fontWeight};
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  color: ${(p) => p.theme.semanticTextBody};
  background: ${(p) =>
    p.$active ? p.theme.buttonSubtleActiveBackground : "transparent"};

  &:hover:not([aria-disabled="true"]) {
    background: ${(p) => p.theme.buttonSubtleHoverBackground};
  }
  &[aria-disabled="true"] {
    cursor: default;
    color: ${(p) => p.theme.semanticTextDisabled};
  }
`;
